/** Pure edits to a project's teams and people. Each returns a new config; validate with projectConfigSchema before saving. */
const clone = (v) => JSON.parse(JSON.stringify(v));
export function addTeam(config, team) {
    const c = clone(config);
    const id = c.teams.reduce((m, t) => Math.max(m, t.id), -1) + 1;
    c.teams.push({ id, name: team.name.trim(), color: team.color, members: [] });
    return { config: c, id };
}
export function updateTeam(config, id, patch) {
    const c = clone(config);
    const t = c.teams.find(x => x.id === id);
    if (!t)
        throw new Error(`Unknown team ${id}`);
    if (patch.name !== undefined)
        t.name = patch.name.trim();
    if (patch.color !== undefined)
        t.color = patch.color;
    return c;
}
/** Removes an empty team; anything that pointed at it falls back to the first remaining team. */
export function removeTeam(config, id) {
    const c = clone(config);
    const t = c.teams.find(x => x.id === id);
    if (!t)
        throw new Error(`Unknown team ${id}`);
    if (t.members.length)
        throw new Error(`${t.name} still has people; move them to another team first`);
    if (c.teams.length === 1)
        throw new Error("A project needs at least one team");
    c.teams = c.teams.filter(x => x.id !== id);
    const fallback = c.defaultOwnerTeam === id ? c.teams[0].id : c.defaultOwnerTeam;
    c.defaultOwnerTeam = fallback;
    for (const k of Object.keys(c.workstreamOwners))
        if (c.workstreamOwners[k] === id)
            c.workstreamOwners[k] = fallback;
    for (const r of c.workstreamOwnerRules)
        if (r.team === id)
            r.team = fallback;
    for (const k of Object.keys(c.homeTeam))
        if (c.homeTeam[k] === id)
            delete c.homeTeam[k];
    return c;
}
function setTeams(c, key, teams) {
    for (const t of c.teams) {
        const want = teams.includes(t.id);
        const has = t.members.includes(key);
        if (want && !has)
            t.members.push(key);
        if (!want && has)
            t.members = t.members.filter(m => m !== key);
    }
    if (teams.length > 1)
        c.homeTeam[key] = teams[0];
    else
        delete c.homeTeam[key];
}
function applyPerson(r, p) {
    if (p.firstName !== undefined) {
        const first = p.firstName.trim(), last = (p.lastName ?? r.lastName ?? "").trim();
        r.firstName = first;
        if (last)
            r.lastName = last;
        else
            delete r.lastName;
        r.name = [first, last].filter(Boolean).join(" ");
    }
    else if (p.name !== undefined)
        r.name = p.name.trim();
    if (p.nickname !== undefined) {
        if (p.nickname.trim())
            r.nickname = p.nickname.trim();
        else
            delete r.nickname;
    }
    if (p.discipline !== undefined)
        r.discipline = p.discipline;
    if (p.extraDisciplines !== undefined) {
        const extra = [...new Set(p.extraDisciplines.filter(d => d !== r.discipline))];
        if (extra.length)
            r.extraDisciplines = extra;
        else
            delete r.extraDisciplines;
    }
    else if (r.extraDisciplines) {
        r.extraDisciplines = r.extraDisciplines.filter(d => d !== r.discipline);
        if (!r.extraDisciplines.length)
            delete r.extraDisciplines;
    }
    if (p.fte !== undefined)
        r.fte = p.fte;
    if (p.userId !== undefined) {
        if (p.userId)
            r.userId = p.userId;
        else
            delete r.userId;
    }
}
export function addPerson(config, p) {
    const c = clone(config);
    const taken = new Set(c.resources.map(r => r.key));
    let n = c.resources.length + 1;
    while (taken.has(`p${n}`))
        n++;
    const key = `p${n}`;
    const r = { key, name: "", discipline: p.discipline, fte: p.fte };
    applyPerson(r, p);
    c.resources.push(r);
    setTeams(c, key, p.teams);
    return { config: c, key };
}
export function updatePerson(config, key, p) {
    const c = clone(config);
    const r = c.resources.find(x => x.key === key);
    if (!r)
        throw new Error(`Unknown person ${key}`);
    applyPerson(r, p);
    if (p.teams)
        setTeams(c, key, p.teams);
    return c;
}
/** Removes a person from the roster and every team. The server refuses this while they still have work on the board. */
export function removePerson(config, key) {
    const c = clone(config);
    c.resources = c.resources.filter(r => r.key !== key);
    for (const t of c.teams)
        t.members = t.members.filter(m => m !== key);
    delete c.homeTeam[key];
    c.nameAliases = c.nameAliases.filter(([, k]) => k !== key);
    return c;
}
//# sourceMappingURL=config-edit.js.map